/**
 * A person's own language setting — rung (a), the top of the chain.
 *
 * IT RENDERS IN THE LANGUAGE BEING CHOSEN, which is the only property that matters here. Somebody who
 * has landed in a language they cannot read has to be able to get themselves out, and a settings card
 * written in the language they are trying to leave is a locked door. Hence: the names of the
 * languages in their own words, and the card's own labels through `useT`.
 *
 * "USE MY DEVICE'S LANGUAGE" IS NOT A THIRD LANGUAGE — it CLEARS the preference. That distinction is
 * the whole reason the column is nullable: cleared means "never chosen", which hands the decision
 * back to the phone and the company default, and it is what somebody who picked a language by mistake
 * actually wants. A card that could only ever add a choice would have no undo.
 *
 * The save is fire-and-forget by design (see LanguageContext.setLanguage): the language changes on
 * screen immediately and the server catches up. A failed write means the choice is device-local for
 * this session, which beats refusing to change language because a network call did not land.
 */
import React, { useCallback, useState } from 'react'

import apiClient from '../../api/index.js'
import { useLanguage, useT } from './LanguageContext.jsx'
import { languageName, selectedLanguage } from './languages'

export default function LanguagePreferenceCard() {
  const t = useT('settings')
  const { lang, offered, accountLanguage, setLanguage, clearLanguage } = useLanguage()
  const [status, setStatus] = useState(null)

  // What this account's stored tag means among the languages on offer. NOT `accountLanguage ===
  // tag`: the server accepts a regional tag (`es-MX`), the resolver renders it as Spanish, and an
  // exact comparison left this card — the one screen whose entire job is to show somebody their own
  // preference and let them out of it — with nothing selected while the page around it was Spanish.
  const selected = selectedLanguage(accountLanguage, offered)

  const persist = useCallback(async (tag) => {
    try {
      await apiClient.put('/user/me/language', { language: tag })
      setStatus('saved')
    } catch {
      setStatus('failed')
    }
  }, [])

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-900">{t('languageTitle')}</h3>
      <p className="text-xs text-gray-500 mt-1 mb-3">{t('languageHelp')}</p>

      <div className="space-y-2">
        {offered.map((tag) => (
          <label key={tag} className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
            <input
              type="radio"
              name="language-preference"
              checked={selected === tag}
              onChange={() => { setStatus(null); setLanguage(tag, { persist }) }}
            />
            {/* The language's own name. Never a code, never a flag — see LanguageToggle.jsx. */}
            <span>{languageName(tag)}</span>
          </label>
        ))}

        <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
          <input
            type="radio"
            name="language-preference"
            checked={!selected}
            onChange={() => { setStatus(null); clearLanguage({ persist }) }}
            className="mt-1"
          />
          <span>
            {t('useMyDeviceLanguage')}
            <span className="block text-xs text-gray-500">
              {t('useMyDeviceLanguageHelp')}
              {' '}
              {/* What that actually resolves to right now, so "let the device decide" is not a
                  guess the person has to make blind. */}
              ({languageName(lang)})
            </span>
          </span>
        </label>
      </div>

      {status === 'saved' && (
        <p className="text-xs text-green-700 mt-3">{t('languageSaved')}</p>
      )}
      {status === 'failed' && (
        <p className="text-xs text-red-700 mt-3">{t('languageSaveFailed')}</p>
      )}
    </div>
  )
}
